import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, MapPin, Briefcase, Filter } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import api from '../services/api';

const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterLocation, setFilterLocation] = useState('');

  useEffect(() => {
    fetchJobs();
  }, [searchTerm, filterType, filterLocation]);

  const fetchJobs = async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterType) params.job_type = filterType;
      if (filterLocation) params.location = filterLocation;

      const data = await api.jobs.getAll(params);
      setJobs(data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      <div className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="jobs-heading">
              Explore <span className="gradient-text">Opportunities</span>
            </h1>
            <p className="text-lg text-slate-400">Find your perfect role from our curated job listings</p>
          </div>

          <div className="glass-card rounded-2xl p-6 mb-8" data-testid="search-filters">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 glass-input rounded-lg text-slate-200"
                  data-testid="search-input"
                />
              </div>

              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 glass-input rounded-lg text-slate-200"
                  data-testid="job-type-filter"
                >
                  <option value="">All Types</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Remote">Remote</option>
                </select>
              </div>

              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Location"
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 glass-input rounded-lg text-slate-200"
                  data-testid="location-filter"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12" data-testid="no-jobs-message">
              <p className="text-slate-400 text-lg">No jobs found. Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6" data-testid="jobs-list">
              {jobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link to={`/jobs/${job.id}`}>
                    <div className="group glass-card rounded-2xl p-6 hover:border-cyan-500/50 transition-all cursor-pointer" data-testid={`job-card-${job.id}`}>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-semibold mb-2 group-hover:text-cyan-500 transition-colors">
                            {job.title}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4" />
                              {job.job_type}
                            </span>
                          </div>
                        </div>
                        <span className="px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-500 text-xs font-mono">
                          ${job.salary_range}
                        </span>
                      </div>

                      <p className="text-slate-400 line-clamp-2 mb-4">{job.description}</p>

                      <div className="flex flex-wrap gap-2">
                        {job.requirements.slice(0, 3).map((req, idx) => (
                          <span key={idx} className="px-3 py-1 rounded-full bg-slate-800 text-slate-300 text-xs">
                            {req}
                          </span>
                        ))}
                        {job.requirements.length > 3 && (
                          <span className="px-3 py-1 rounded-full bg-slate-800 text-slate-300 text-xs">
                            +{job.requirements.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default JobsPage;